import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { CellCardDto } from '@eombor/contracts';
import { useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { warehouseApi } from '../api/warehouse.api.js';

export interface CellScan {
  /** Kodni izlaydi. Bo'sh kod e'tiborsiz qoladi. */
  scan: (code: string) => void;
  pending: boolean;
  /**
   * Bittadan ko'p ombordan topilgan kataklar — tanlash ro'yxati shu yerdan
   * chiziladi. Bitta topilsa bu bo'sh qoladi: kartochka darhol ochiladi.
   */
  choices: CellCardDto[];
  clearChoices: () => void;
  /** Topilmagan kod — ekran uni QIZIL qilib ushlab turadi. Topilsa `null`. */
  notFound: string | null;
}

/**
 * SKANERLANGAN KODNI KATAKKA aylantirish — bitta joyda.
 *
 * Mantiq komponentda emas, shu yerda: qaror uch holatdan iborat va u
 * chaqiruvchidan qat'i nazar bir xil bo'lishi kerak.
 *
 *   - bitta topildi  → darhol ochiladi;
 *   - bir nechta     → server TAXMIN QILMAYDI, tanlashni odam hal qiladi;
 *   - topilmadi      → XATO EMAS. Skanerning noto'g'ri o'qishi kundalik hol;
 *                      ekran buni ramkada ko'rsatadi, xato oynasi chiqarmaydi.
 *
 * Toast faqat TARMOQ xatosida chiqadi — u haqiqatan ham kutilmagan hodisa.
 */
export const useCellScan = (onFound: (cell: CellCardDto) => void): CellScan => {
  const toast = useToast();
  const [choices, setChoices] = useState<CellCardDto[]>([]);
  const [notFound, setNotFound] = useState<string | null>(null);

  const lookup = useMutation({
    mutationFn: (code: string) => warehouseApi.scanCell(code),
    onSuccess: (matches, code) => {
      const single = matches[0];
      if (single === undefined) {
        setNotFound(code);
        return;
      }
      if (matches.length > 1) {
        setChoices(matches);
        return;
      }
      onFound(single);
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('warehouse.scanFailed'),
        'danger',
      );
    },
  });

  return {
    scan: (code) => {
      const trimmed = code.trim().toUpperCase();
      if (trimmed === '') return;
      setChoices([]);
      setNotFound(null);
      lookup.mutate(trimmed);
    },
    pending: lookup.isPending,
    choices,
    clearChoices: () => setChoices([]),
    notFound,
  };
};
